import React, { useState, useEffect, useRef } from "react"
import { useSelector } from "react-redux"
import { useLocation } from "react-router-dom"
import ContentWithSidebars from "../../components/templates/ContentWithSidebars"
import AccountSidebarNav from "../../components/AccountSidebarNav"
import AccountSubscriptionContainer from "../../containers/AccountSubscriptionContainer"
import Loading from "../../components/Loading/Loading"
import services from "../../services"
import axios from "../../services/axios"
import { createCheckoutSession } from "../../services/subscriptionServices"
import { INDIVIDUAL } from "../../helpers/userRoles"
import styles from "./index.module.scss"

const AccountSubscriptionPage = () => {
  const userInfo = useSelector((state) => state.user)
  const isProfessional = userInfo.role === INDIVIDUAL
  const location = useLocation()
  const checkoutProcessed = useRef(false)
  
  const [state, setState] = useState({
    isLoading: true,
    companyProfile: {},
    paymentAccountUrl: "",
  })

  useEffect(() => {
    const query = new URLSearchParams(location.search)
    const msg = query.get("msg")
    const sessionId = query.get("session_id")

    const loadSubscriptionData = () => {
      const subscriptionPromise = isProfessional
        ? services.userServices.getSelfSubscriptionInfo()
        : services.companyServices.getSelfCompanyInfo()

      return subscriptionPromise
        .then(({ data }) => {
          const companyProfile = data
          setState((prevState) => ({
            ...prevState,
            companyProfile,
            isLoading: false,
          }))
          return companyProfile
        })
        .then((companyProfile) => {
          if (!isProfessional && companyProfile.paid_subscription_status !== "none") {
            return services.companyServices.getPaymentUrl()
          }
        })
        .then((url) => {
          if (url) {
            setState((prevState) => ({ ...prevState, paymentAccountUrl: url }))
          }
        })
        .catch((error) => {
          console.error("Error fetching subscription info:", error)
          setState((prevState) => ({
            ...prevState,
            isLoading: false,
          }))
        })
    }

    if (msg === "checkout.success" && sessionId && !checkoutProcessed.current) {
      checkoutProcessed.current = true
      axios
        .post("/integrations/stripe/complete-checkout", { session_id: sessionId })
        .then(() => {
          window.history.replaceState({}, "", "/subscription")
          return loadSubscriptionData()
        })
        .catch((error) => {
          console.error("Error completing checkout:", error)
          loadSubscriptionData()
        })
    } else {
      loadSubscriptionData()
    }
  }, [isProfessional, location.search])

  const onClickToBuySubscription = () => {
    if (isProfessional) {
      const email = userInfo.email
      localStorage.setItem("userEmail", email)
      createCheckoutSession("professional", 1, false, undefined, "annual", email)
        .then((checkoutUrl) => {
          window.location.href = checkoutUrl
        })
        .catch((error) => {
          console.error("Error creating checkout session:", error)
          alert("Something went wrong while loading the checkout page. Please try again later.")
        })
      return
    }
    services.companyServices
      .getPaymentUrl()
      .then((url) => {
        if (url) {
          window.location.href = url
        } else {
          alert("Could not retrieve checkout link. Please try again later.")
        }
      })
      .catch((error) => {
        console.error("Error getting payment URL:", error)
        alert("Something went wrong while loading the checkout page. Please try again later.")
      })
  }

  const onPauseSubscription = () =>
    services.companyServices.pauseSubscription().then((msg) => {
      if (msg === "ok") {
        setState((prevState) => ({
          ...prevState,
          companyProfile: {
            ...prevState.companyProfile,
            paid_subscription_status: "paused",
          },
        }))
      }
    })

  const onResumeSubscription = () =>
    services.companyServices.resumeSubscription().then((msg) => {
      if (msg === "ok") {
        setState((prevState) => ({
          ...prevState,
          companyProfile: {
            ...prevState.companyProfile,
            paid_subscription_status: "active",
          },
        }))
      }
    })

  const onClickToGoToPaymentAccount = () =>
    window.open(state.paymentAccountUrl, "_blank")

  return (
    <div className={styles.pageWrap}>
      <ContentWithSidebars topSidebar={() => <AccountSidebarNav />}>
        <div className={styles.settingsContent}>
          {state.isLoading ? (
            <Loading />
          ) : (
            <>
              {state.companyProfile && (
                <AccountSubscriptionContainer
                  companyProfile={state.companyProfile}
                  paymentAccountUrl={state.paymentAccountUrl}
                  onClickToBuySubscription={onClickToBuySubscription}
                  onClickToGoToPaymentAccount={onClickToGoToPaymentAccount}
                  onPauseSubscription={onPauseSubscription}
                  onResumeSubscription={onResumeSubscription}
                />
              )}
            </>
          )}
        </div>
      </ContentWithSidebars>
    </div>
  )
}

export default AccountSubscriptionPage
